import { Formik } from 'formik';
import { Button, Input, Switch, HelperText } from '@yaradigitallabs/ahua-react';
import * as Yup from 'yup';
import { useState } from 'react';

interface BackfillFormProps {
  onSubmit: (values: {
    startTime: string;
    endTime: string;
    batchSize?: number;
    retry?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extraFields?: Record<string, any>;
  }) => void;
}

const validationSchema = Yup.object().shape({
  startTime: Yup.string().required('Start time is required'),
  endTime: Yup.string()
    .required('End time is required')
    .test(
      'is-after-start',
      'End time must be after start time',
      function (value) {
        const { startTime } = this.parent;
        return !startTime || !value || new Date(value) > new Date(startTime);
      }
    )
    .test('is-before-now', 'End time must be before now', function (value) {
      if (!value) return true;
      return new Date(value) < new Date();
    }),
  batchSize: Yup.number().min(1, 'Batch size must be at least 1').nullable(),
  retry: Yup.number().min(0, 'Retry must be 0 or more').nullable(),
});

export const BackfillForm = ({ onSubmit }: BackfillFormProps) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [extraFieldRows, setExtraFieldRows] = useState<
    { key: string; value: string }[]
  >([]);

  const addExtraField = () => {
    setExtraFieldRows(prev => [...prev, { key: '', value: '' }]);
  };

  const removeExtraField = (index: number) => {
    setExtraFieldRows(prev => prev.filter((_, i) => i !== index));
  };

  const updateExtraField = (
    index: number,
    field: 'key' | 'value',
    val: string
  ) => {
    setExtraFieldRows(prev =>
      prev.map((row, i) => (i === index ? { ...row, [field]: val } : row))
    );
  };

  const removeEmptyExtraField = () => {
    setExtraFieldRows(prev =>
      prev.filter(row => row.key.trim() || row.value.trim())
    );
  };

  const buildExtraFields = (): Record<string, string> => {
    const obj: Record<string, string> = {};
    extraFieldRows.forEach(({ key, value }) => {
      if (key.trim()) obj[key.trim()] = value;
    });
    return obj;
  };

  return (
    <Formik
      initialValues={{
        startTime: '',
        endTime: '',
        batchSize: undefined as number | undefined,
        retry: undefined as number | undefined,
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => {
        onSubmit({
          ...values,
          extraFields: buildExtraFields(),
        });
        resetForm();
        setExtraFieldRows([]);
      }}
    >
      {({ handleSubmit, values, handleChange, errors, touched }) => (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4 w-full items-center"
        >
          <div className="flex flex-row items-end gap-6 w-full justify-center">
            <Input
              label="Start Time"
              type="datetime-local"
              name="startTime"
              value={values.startTime}
              onChange={handleChange}
              error={
                touched.startTime && errors.startTime
                  ? errors.startTime
                  : undefined
              }
              required
              onClick={() => {
                const input = document.getElementsByName(
                  'startTime'
                )[0] as HTMLInputElement;
                input?.focus();
                input?.showPicker?.();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  const input = document.getElementsByName(
                    'startTime'
                  )[0] as HTMLInputElement;
                  input?.focus();
                  input?.showPicker?.();
                }
              }}
            />
            <Input
              label="End Time"
              type="datetime-local"
              name="endTime"
              value={values.endTime}
              onChange={handleChange}
              error={
                touched.endTime && errors.endTime ? errors.endTime : undefined
              }
              required
              onClick={() => {
                const input = document.getElementsByName(
                  'endTime'
                )[0] as HTMLInputElement;
                input?.focus();
                input?.showPicker?.();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  const input = document.getElementsByName(
                    'endTime'
                  )[0] as HTMLInputElement;
                  input?.focus();
                  input?.showPicker?.();
                }
              }}
            />
            {(touched.startTime && errors.startTime) ||
            (touched.endTime && errors.endTime) ? (
              <div className="text-red-500 text-xs mt-1">
                {errors.startTime || errors.endTime}
              </div>
            ) : null}
            <div className="flex flex-col items-start">
              <Button
                style={{ height: '40px' }}
                size="s"
                variant="primary"
                type="submit"
              >
                Backfill
              </Button>
            </div>
          </div>

          {/* Advanced Options Toggle */}
          <div className="w-full max-w-2xl">
            <Switch
              label="Show advanced options"
              checked={showAdvanced}
              onCheckedChange={() => {
                setShowAdvanced(prevState => !prevState);
                removeEmptyExtraField();
              }}
            />
            <div
              className={`overflow-y-auto transition-all duration-300 ease-in-out ${
                showAdvanced
                  ? 'max-h-[500px] opacity-100 transform translate-y-0'
                  : 'max-h-0 opacity-0 transform -translate-y-2'
              }`}
              style={{
                transitionProperty: 'max-height, opacity, transform',
              }}
            >
              <div className="flex flex-col gap-4 pt-4">
                <div className="flex flex-row gap-4">
                  <Input
                    label="Batch Size"
                    type="number"
                    name="batchSize"
                    helperText="Number of records per batch"
                    value={values.batchSize ?? ''}
                    onChange={handleChange}
                    error={
                      touched.batchSize && errors.batchSize
                        ? errors.batchSize
                        : undefined
                    }
                    size="xs"
                    cover="fill"
                  />
                  <Input
                    label="Retry"
                    type="number"
                    name="retry"
                    helperText="Number of retry attempts"
                    value={values.retry ?? ''}
                    onChange={handleChange}
                    error={
                      touched.retry && errors.retry ? errors.retry : undefined
                    }
                    size="xs"
                    cover="fill"
                  />
                </div>

                {/* Extra Fields - Key/Value pairs */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold">Extra Fields</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      onClick={addExtraField}
                    >
                      + Add Field
                    </Button>
                  </div>
                  <HelperText colorConcept="neutral" icon="Info">
                    Add custom key-value pairs to include additional parameters
                    in the backfill request
                  </HelperText>
                  {extraFieldRows.map((row, index) => (
                    <div
                      key={index}
                      className="flex flex-row gap-2 items-start"
                    >
                      <Input
                        label="Key"
                        value={row.key}
                        onChange={e =>
                          updateExtraField(index, 'key', e.target.value)
                        }
                        cover="fill"
                        size="xs"
                      />
                      <Input
                        label="Value"
                        value={row.value}
                        onChange={e =>
                          updateExtraField(index, 'value', e.target.value)
                        }
                        cover="fill"
                        size="xs"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        onClick={() => removeExtraField(index)}
                        aria-label="Remove extra field"
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};
